import { useEffect, useState } from "react"

import { checkUsernameAvailability } from "@/services/authApi"
import { isValidUsername } from "@/utils/authFieldValidation"

export type UsernameAvailabilityStatus =
  | "idle"
  | "checking"
  | "available"
  | "taken"
  | "invalid"
  | "error"

const DEBOUNCE_MS = 550

export function useUsernameAvailability(username: string): UsernameAvailabilityStatus {
  const [status, setStatus] = useState<UsernameAvailabilityStatus>("idle")

  useEffect(() => {
    if (!username) {
      setStatus("idle")
      return
    }
    if (!isValidUsername(username)) {
      setStatus("invalid")
      return
    }

    let cancelled = false
    const controller = new AbortController()
    setStatus("checking")

    const timer = window.setTimeout(() => {
      void checkUsernameAvailability(username, controller.signal)
        .then((result) => {
          if (cancelled) return
          if (result.reason === "available" && result.available) {
            setStatus("available")
            return
          }
          if (result.reason === "invalid") {
            setStatus("invalid")
            return
          }
          if (result.reason === "taken" || result.available === false) {
            setStatus("taken")
            return
          }
          setStatus("error")
        })
        .catch(() => {
          if (cancelled) return
          // Aborted because the username changed or the component unmounted.
          if (controller.signal.aborted) return
          // Timeout / 429 / network / non-OK — never claim available.
          setStatus("error")
        })
    }, DEBOUNCE_MS)

    return () => {
      cancelled = true
      controller.abort()
      window.clearTimeout(timer)
    }
  }, [username])

  return status
}
